/**
 * Renders all screenspace sketch strokes in the scene as Line2 ribbons.
 * Mounted once at scene root alongside `<StrokeOverlay/>`, which renders
 * projected strokes.
 *
 * Screenspace strokes carry world-space points baked at draw time; there
 * is no host mesh, no skinning, no surface offset. Render order is set so
 * screenspace strokes draw on top of projected strokes within the same
 * layer — matching the "annotation overlay" UX convention.
 */

import { useFrame, useThree } from "@react-three/fiber";
import { useEffect, useMemo, useRef } from "react";
import { LineSegmentsGeometry } from "three/examples/jsm/lines/LineSegmentsGeometry.js";
import { LineSegments2 } from "three/examples/jsm/lines/webgpu/LineSegments2.js";
import * as THREE from "three/webgpu";
import { useStore } from "../app/store.ts";
import {
  isScreenspaceStroke,
  type SketchStroke,
  type ScreenspaceStroke,
} from "../modeling/sketch.ts";
import { screenspaceStrokeWorldPositions } from "../sketch/sceneSketch.ts";
import { setFatLinePositions } from "./objectHelperLines.ts";

const LEGACY_STROKE_WORLD_THICKNESS_PER_PX = 0.001;

interface StrokeRenderState {
  geo: LineSegmentsGeometry;
  mat: THREE.Line2NodeMaterial;
  line: LineSegments2;
  worldBuf: Float32Array;
  segBuf: Float32Array;
  capacity: number;
  lastColor: string;
  lastThickness: number;
  lastCompositeOpacity: number;
}

function nextCapacity(required: number, current: number): number {
  let cap = Math.max(current, 64);
  while (cap < required) cap *= 2;
  return cap;
}

function strokeWorldThickness(stroke: SketchStroke): number {
  const w = stroke.worldThickness;
  if (w != null && Number.isFinite(w)) return Math.max(1e-5, w);
  return Math.max(
    1e-5,
    stroke.thickness * LEGACY_STROKE_WORLD_THICKNESS_PER_PX,
  );
}

function swapGeometry(rs: StrokeRenderState): void {
  // See StrokeOverlay.tsx swapStrokeGeometry comment — WebGPU keeps stale
  // instanced buffers bound across capacity-growth attribute swaps.
  const old = rs.geo;
  const next = new LineSegmentsGeometry();
  rs.geo = next;
  rs.line.geometry = next;
  old.dispose();
}

function fillSafeTail(
  segBuf: Float32Array,
  validSegments: number,
  capacity: number,
): void {
  if (validSegments >= capacity) return;
  const src = validSegments > 0 ? (validSegments - 1) * 6 + 3 : 0;
  const x = segBuf[src] ?? 0;
  const y = segBuf[src + 1] ?? 0;
  const z = segBuf[src + 2] ?? 0;
  const eps = 1e-5;
  for (let i = validSegments; i < capacity; i++) {
    const o = i * 6;
    segBuf[o] = x;
    segBuf[o + 1] = y;
    segBuf[o + 2] = z;
    segBuf[o + 3] = x + eps;
    segBuf[o + 4] = y;
    segBuf[o + 5] = z;
  }
}

function writeSegments(
  worldBuf: Float32Array,
  segBuf: Float32Array,
  pointCount: number,
): void {
  for (let i = 0; i < pointCount - 1; i++) {
    const a = i * 3;
    const b = (i + 1) * 3;
    const o = i * 6;
    segBuf[o] = worldBuf[a];
    segBuf[o + 1] = worldBuf[a + 1];
    segBuf[o + 2] = worldBuf[a + 2];
    segBuf[o + 3] = worldBuf[b];
    segBuf[o + 4] = worldBuf[b + 1];
    segBuf[o + 5] = worldBuf[b + 2];
  }
}

function hasFiniteValues(buf: Float32Array, length: number): boolean {
  for (let i = 0; i < length; i++) {
    if (!Number.isFinite(buf[i])) return false;
  }
  return true;
}

export function ScreenspaceSketchOverlay() {
  const sketch = useStore((s) => s.sketch);
  const sketchStrokeSeq = useStore((s) => s.sketchStrokeSeq);
  const { scene, invalidate } = useThree();

  useEffect(() => {
    invalidate();
  }, [sketchStrokeSeq, invalidate]);

  const rsMapRef = useRef(new Map<string, StrokeRenderState>());
  const rootRef = useRef<THREE.Group | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: sketchStrokeSeq forces re-enumeration during in-place stroke mutation
  const visibleStrokes = useMemo(() => {
    const out: { stroke: ScreenspaceStroke; layerOpacity: number }[] = [];
    for (const layer of sketch.layers) {
      if (!layer.visible) continue;
      for (const stroke of layer.strokes) {
        if (!isScreenspaceStroke(stroke)) continue;
        out.push({ stroke, layerOpacity: layer.opacity });
      }
    }
    return out;
  }, [sketch, sketchStrokeSeq]);

  useEffect(() => {
    const group = new THREE.Group();
    // Render after projected overlays (renderOrder 5) so screenspace
    // strokes draw over surface annotations within the same scene.
    group.renderOrder = 6;
    group.userData.strokeOverlayRoot = true;
    group.userData.excludeFromExport = true;
    group.userData.sketchOverlay = true;
    rootRef.current = group;
    scene.add(group);
    return () => {
      scene.remove(group);
      rootRef.current = null;
    };
  }, [scene]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const rsMap = rsMapRef.current;
    const keep = new Set<string>();

    for (const { stroke, layerOpacity } of visibleStrokes) {
      keep.add(stroke.id);
      let rs = rsMap.get(stroke.id);
      const compositeOpacity = stroke.opacity * layerOpacity;
      const lineWidth = strokeWorldThickness(stroke);
      if (!rs) {
        const geo = new LineSegmentsGeometry();
        const mat = new THREE.Line2NodeMaterial({
          color: new THREE.Color(stroke.color).getHex(),
          linewidth: lineWidth,
          transparent: true,
          opacity: compositeOpacity,
          depthTest: true,
          depthWrite: false,
        });
        mat.worldUnits = true;
        const line = new LineSegments2(geo, mat);
        line.frustumCulled = false;
        line.raycast = () => {};
        line.userData.excludeFromExport = true;
        line.userData.sketchOverlay = true;
        root.add(line);
        rs = {
          geo,
          mat,
          line,
          worldBuf: new Float32Array(0),
          segBuf: new Float32Array(0),
          capacity: 0,
          lastColor: stroke.color,
          lastThickness: lineWidth,
          lastCompositeOpacity: compositeOpacity,
        };
        rsMap.set(stroke.id, rs);
      } else {
        if (rs.lastColor !== stroke.color) {
          rs.mat.color.set(stroke.color);
          rs.lastColor = stroke.color;
        }
        if (rs.lastThickness !== lineWidth) {
          rs.mat.linewidth = lineWidth;
          rs.lastThickness = lineWidth;
        }
        if (rs.lastCompositeOpacity !== compositeOpacity) {
          rs.mat.opacity = compositeOpacity;
          rs.lastCompositeOpacity = compositeOpacity;
        }
      }
    }

    for (const [id, rs] of rsMap) {
      if (keep.has(id)) continue;
      root.remove(rs.line);
      rs.geo.dispose();
      rs.mat.dispose();
      rsMap.delete(id);
    }
  }, [visibleStrokes]);

  useEffect(() => {
    const rsMap = rsMapRef.current;
    return () => {
      for (const rs of rsMap.values()) {
        rs.geo.dispose();
        rs.mat.dispose();
      }
      rsMap.clear();
    };
  }, []);

  useFrame(() => {
    const rsMap = rsMapRef.current;
    for (const { stroke } of visibleStrokes) {
      const rs = rsMap.get(stroke.id);
      if (!rs) continue;
      const pointCount = stroke.points.length;
      if (pointCount < 2) {
        rs.line.visible = false;
        rs.geo.instanceCount = 0;
        continue;
      }
      rs.line.visible = true;
      const segments = pointCount - 1;
      if (segments > rs.capacity) {
        const newCap = nextCapacity(segments, rs.capacity);
        rs.worldBuf = new Float32Array((newCap + 1) * 3);
        rs.segBuf = new Float32Array(newCap * 6);
        rs.capacity = newCap;
        swapGeometry(rs);
      }
      screenspaceStrokeWorldPositions(stroke, rs.worldBuf);
      writeSegments(rs.worldBuf, rs.segBuf, pointCount);
      fillSafeTail(rs.segBuf, segments, rs.capacity);
      if (!hasFiniteValues(rs.segBuf, rs.capacity * 6)) continue;
      try {
        setFatLinePositions(rs.geo, rs.segBuf, null, false);
        rs.geo.instanceCount = segments;
      } catch {
        // Skip frame; retry next.
      }
    }
  });

  return null;
}
